/**
 * Preisfindung nach dem Kalkulationsmodell der Mühle Engelke.
 *
 * Vom Kunden beschrieben, hier eins zu eins abgebildet:
 *
 *   ROHSTOFF     = Σᵢ anteilᵢ × (Notierung(sorteᵢ) − Abschlagᵢ + Prämie(sorteᵢ))
 *   WZ_BEREINIGT = ROHSTOFF + Mahllohn − Nachprodukterlös
 *   AB_HOF       = WZ_BEREINIGT ÷ Ausbeute
 *
 *   TOUR         = Personal + LKW + Kraftstoff + Maut
 *   FRACHT_JE_T  = TOUR ÷ Lademenge
 *
 *   FINANZIERUNG = (AB_HOF + FRACHT) × Zinssatz × Zahlungsziel ÷ 360
 *   PREIS        = AB_HOF + FRACHT_JE_T + FINANZIERUNG
 *
 * Zwei Eigenheiten, die beim Lesen leicht untergehen und deshalb hier
 * festgehalten sind:
 *
 * 1. Der Mahllohn ist je Tonne KORN gemeint und wird durch die Ausbeute
 *    mitdividiert. Aus 50 €/t Weizen werden 64,10 €/t Mehl. Wer die 50 €
 *    als „je Tonne Mehl" liest, liegt um 28 % daneben.
 *
 * 2. Die Fracht ist ein TOURPREIS, kein Tonnenpreis. Dieselbe Fahrt kostet
 *    bei 25 t 33,87 €/t und bei 5 t 169,36 €/t. Die Lademenge gehört
 *    deshalb zwingend in die Rechnung.
 *
 * Alle Beträge ganzzahlig in e4 (1/10000 EUR), wie überall in dieser
 * Anwendung. Prozente in Basispunkten: 7800 = 78,00 %.
 */

import { roundDiv } from "./money.js";

/* ── Weizennotierung ────────────────────────────────────────────────────── */

export type Kontraktmonat = "mrz" | "mai" | "dez";

/**
 * Liefermonat → Kontraktmonat.
 *
 *   Q1  (1–3)   ← März
 *   Q2  (4,5)   ← Mai
 *   Q3  (6,7)   ← Mai
 *   Q3  (8)     ← Dezember, meist mit Abschlag
 *   Q4  (9–12)  ← Dezember
 *
 * Entscheidend: Es zählt der LIEFERmonat, nicht der Angebotstag. Ein im
 * August erstelltes Angebot für Lieferung im Oktober rechnet mit Dezember.
 */
export function kontraktmonat(liefermonat: number): Kontraktmonat {
  if (!Number.isInteger(liefermonat) || liefermonat < 1 || liefermonat > 12)
    throw new Error(`Ungültiger Liefermonat: ${liefermonat}`);
  if (liefermonat <= 3) return "mrz";
  if (liefermonat <= 7) return "mai";
  return "dez";
}

/** Nur der August bekommt den Abschlag — so beschrieben. */
export const hatAbschlag = (liefermonat: number) => liefermonat === 8;

export const KONTRAKT_TEXT: Record<Kontraktmonat, string> = {
  mrz: "März",
  mai: "Mai",
  dez: "Dezember",
};

/** Kalendermonat je Kontrakt. MATIF Weizen notiert in diesen Monaten. */
export const KONTRAKT_MONAT: Record<Kontraktmonat, number> = { mrz: 3, mai: 5, dez: 12 };

/**
 * Nächste Fälligkeit eines Kontraktmonats, vom Stichtag aus gesehen.
 *
 * Ohne Jahreszahl ist „MATIF Weizen März" mehrdeutig: Im August ist der
 * nächste Märzkontrakt der des Folgejahres, der nächste Dezemberkontrakt
 * dagegen der des laufenden. Wer die Notierung pflegt, muss wissen, welchen
 * Kontrakt er ablesen soll — ein Jahr Unterschied sind schnell zwanzig Euro
 * je Tonne.
 *
 * Der laufende Monat zählt noch als „nächster": Der Märzkontrakt läuft bis
 * Mitte März und wird bis dahin gehandelt.
 */
export function naechsteFaelligkeit(
  kontrakt: Kontraktmonat,
  jetzt: number,
): { monat: number; jahr: number } {
  const d = new Date(jetzt);
  const monat = KONTRAKT_MONAT[kontrakt];
  return {
    monat,
    jahr: d.getUTCFullYear() + (monat < d.getUTCMonth() + 1 ? 1 : 0),
  };
}

/* ── Kontraktcodes ──────────────────────────────────────────────────────── */

/**
 * Code der Marktreihe eines bestimmten Kontrakts: `matif_dez_2026`.
 *
 * Der Code traegt das Jahr, nicht der Kalender. Vorher hiess die Reihe
 * `matif_dez`, und welches Jahr gemeint war, rechnete jede Stelle fuer sich
 * aus dem heutigen Datum aus. Damit war dieselbe Reihe im November der
 * Dezember 2026 und im Januar der Dezember 2027 — mit demselben gepflegten
 * Wert. Ein Jahr Unterschied sind schnell zwanzig Euro je Tonne.
 */
export const kontraktCode = (kontrakt: Kontraktmonat, jahr: number): string =>
  `matif_${kontrakt}_${jahr}`;

const CODE_MUSTER = /^matif_(mrz|mai|dez)_(\d{4})$/;

/** Zerlegt einen Kontraktcode. `null`, wenn es keiner ist. */
export function leseKontraktCode(code: string): { kontrakt: Kontraktmonat; jahr: number } | null {
  const m = CODE_MUSTER.exec(code);
  return m ? { kontrakt: m[1] as Kontraktmonat, jahr: Number(m[2]) } : null;
}

/**
 * Kontrakt fuer einen Liefermonat — Monat UND Jahr.
 *
 * Der Kontrakt liegt immer im selben Kalenderjahr wie der Liefermonat: Die
 * Abbildung in `kontraktmonat()` schiebt nie ueber den Jahreswechsel
 * (1–3 → Maerz, 4–7 → Mai, 8–12 → Dezember). Ein Test haelt das fest.
 */
export function kontraktFuer(
  liefermonat: number,
  lieferjahr: number,
): { kontrakt: Kontraktmonat; jahr: number; code: string } {
  const kontrakt = kontraktmonat(liefermonat);
  return { kontrakt, jahr: lieferjahr, code: kontraktCode(kontrakt, lieferjahr) };
}

/**
 * Letzter Liefermonat, der auf diesen Kontrakt zeigt.
 *
 * NICHT der Faelligkeitsmonat der Boerse. Der Maikontrakt laeuft Mitte Mai
 * aus, wird hier aber noch fuer Lieferungen im Juni und Juli herangezogen
 * (`kontraktmonat(7) === "mai"`). Wer nach Boersenfaelligkeit ausblendet,
 * nimmt der Preisfindung im Juni die Notierung weg, die sie braucht.
 */
const LETZTER_LIEFERMONAT: Record<Kontraktmonat, number> = { mrz: 3, mai: 7, dez: 12 };

/**
 * Ist der Kontrakt ueberholt — kann also kein Liefermonat mehr auf ihn
 * zeigen?
 *
 * Das ist der einzige Grund, eine Kontraktreihe von der Kachelwand zu
 * nehmen. Die ZEILE bleibt stehen: Ein deaktivierter Datensatz waere die
 * schlechteste Variante, weil `ladeNotierungen` nicht auf `aktiv` filtert —
 * die Kachel verschwaende, der eingefrorene Wert flosse weiter in jede
 * Rechnung. Ueberholt heisst dagegen: Es GIBT keinen Liefermonat mehr, der
 * die Reihe anfragt.
 */
export function istUeberholt(kontrakt: Kontraktmonat, jahr: number, jetzt: number): boolean {
  const d = new Date(jetzt);
  const letzter = jahr * 12 + LETZTER_LIEFERMONAT[kontrakt];
  return letzter < d.getUTCFullYear() * 12 + (d.getUTCMonth() + 1);
}

/** Chronologischer Sortierschluessel: Jahr und Faelligkeitsmonat. */
export const kontraktSortierung = (kontrakt: Kontraktmonat, jahr: number): number =>
  jahr * 12 + KONTRAKT_MONAT[kontrakt];

/**
 * Die naechsten `anzahl` Kontrakte ab dem Stichtag, chronologisch.
 *
 * Grundlage fuer die Terminkurve: Wer die Kachelwand fuellt und wer prueft,
 * ob ein Kontrakt vorgehalten wird, fragt hier.
 */
export function kommendeKontrakte(
  jetzt: number,
  anzahl: number,
): { kontrakt: Kontraktmonat; jahr: number; code: string }[] {
  const alle: { kontrakt: Kontraktmonat; jahr: number }[] = [];
  const jahr0 = new Date(jetzt).getUTCFullYear();
  // Drei Kontrakte je Jahr — der Vorrat reicht immer, auch fuer anzahl = 24.
  for (let j = jahr0; j <= jahr0 + Math.ceil(anzahl / 3) + 1; j++)
    for (const k of ["mrz", "mai", "dez"] as const)
      if (!istUeberholt(k, j, jetzt)) alle.push({ kontrakt: k, jahr: j });

  return alle
    .sort((a, b) => kontraktSortierung(a.kontrakt, a.jahr) - kontraktSortierung(b.kontrakt, b.jahr))
    .slice(0, anzahl)
    .map((x) => ({ ...x, code: kontraktCode(x.kontrakt, x.jahr) }));
}

/* ── Mehlpreis ab Hof ───────────────────────────────────────────────────── */

/** Vollstaendige Mischung in Basispunkten: 10000 = 100 %. */
export const ANTEIL_SUMME = 10_000;

/**
 * Ein Bestandteil der Rohstoffmischung.
 *
 * Notierung und Praemie kommen bereits aufgeloest herein — welche Marktreihe
 * dahintersteckt, entscheidet die Route anhand von `getreidesorten`. Dieses
 * Modul rechnet nur.
 */
export interface Bestandteil {
  sorte: string;
  /** Anteil in Basispunkten. Die Summe ueber alle Bestandteile ist 10000. */
  anteil_bp: number;
  /** Notierung der Sorte, €/t Korn. */
  notierung_e4: number;
  /**
   * Abschlag, positiv angegeben; wird abgezogen.
   *
   * Er ist KEINE Eigenschaft des Korns, sondern der Kurve: der Carry zwischen
   * Dezemberkontrakt und Augustkasse. Fuer alle Weizenqualitaeten derselbe,
   * fuer eine Kassareihe wie Roggen gar nicht vorhanden — dort rollt nichts.
   * Die Route setzt ihn deshalb nur bei Kontraktnotierungen und nur im
   * August; hier kommt er schon entschieden an.
   */
  abschlag_e4: number;
  /** Qualitaetspraemie, €/t Korn. Darf negativ sein. */
  praemie_e4: number;
}

export interface MehlEingabe {
  /** Mindestens ein Bestandteil, Anteile zusammen genau 10000 bp. */
  bestandteile: Bestandteil[];
  /** Mahllohn je Tonne KORN. Mischungsunabhaengig (Kunde, 31.08.2026). */
  mahllohn_e4: number;
  /** Ausbeute in Basispunkten, 7800 = 78 %. */
  ausbeute_bp: number;
  /** Nachproduktanteil in Basispunkten, 2200 = 22 %. */
  nachprodukt_anteil_bp: number;
  /** Erloes Nachprodukte, €/t. */
  nachprodukt_preis_e4: number;
}

export interface MehlErgebnis {
  bestandteile: Bestandteil[];
  /** Gewichtete Summe ueber die Bestandteile, €/t Korn. */
  rohstoff_e4: number;
  mahllohn_e4: number;
  nachprodukt_erloes_e4: number;
  /** Summe je Tonne Korn, vor der Ausbeuteumrechnung. */
  wz_bereinigt_e4: number;
  /** Je Tonne Mehl. */
  ab_hof_e4: number;
}

/**
 * Gewichteter Rohstoffpreis.
 *
 * EINMAL am Ende gerundet, nicht je Bestandteil. Der Unterschied ist winzig
 * (bei 33/33/34 hoechstens 1 e4), aber es ist beweisbar nie schlechter und
 * einfacher zu lesen.
 *
 * ACHTUNG: Diese Rundung darf nicht eingespart und in die Ausbeutedivision
 * hineingezogen werden. Mit ihr bleibt Faktor 85 783 Luft zu
 * MAX_SAFE_INTEGER, ohne sie nur noch 8,6 — eine angehobene
 * Notierungsobergrenze braeche das lautlos.
 */
export function berechneRohstoff(bestandteile: Bestandteil[]): number {
  if (!bestandteile.length) throw new Error("Keine Getreidemischung hinterlegt.");

  let summe = 0;
  let anteile = 0;
  for (const b of bestandteile) {
    if (!Number.isInteger(b.anteil_bp) || b.anteil_bp <= 0 || b.anteil_bp > ANTEIL_SUMME)
      throw new Error(`Ungültiger Anteil für ${b.sorte}.`);
    anteile += b.anteil_bp;
    summe += b.anteil_bp * (b.notierung_e4 - b.abschlag_e4 + b.praemie_e4);
  }

  // Exakt 100 %, nicht „ungefaehr". Eine Mischung, die sich nicht schliesst,
  // ergaebe einen Preis, der plausibel aussieht und zu niedrig ist.
  if (anteile !== ANTEIL_SUMME)
    throw new Error(
      `Getreidemischung ergibt ${anteile / 100} % statt 100 %.`,
    );

  return roundDiv(summe, ANTEIL_SUMME);
}

export function berechneMehlAbHof(e: MehlEingabe): MehlErgebnis {
  if (!Number.isInteger(e.ausbeute_bp) || e.ausbeute_bp <= 0 || e.ausbeute_bp > 10_000)
    throw new Error("Ausbeute muss zwischen 0 und 100 % liegen.");

  const rohstoff_e4 = berechneRohstoff(e.bestandteile);
  const nachprodukt_erloes_e4 = roundDiv(e.nachprodukt_anteil_bp * e.nachprodukt_preis_e4, 10_000);

  const wz_bereinigt_e4 = rohstoff_e4 + e.mahllohn_e4 - nachprodukt_erloes_e4;

  // Die Division trifft ALLE Bestandteile, auch den Mahllohn — siehe Kopf.
  const ab_hof_e4 = roundDiv(wz_bereinigt_e4 * 10_000, e.ausbeute_bp);

  return {
    bestandteile: e.bestandteile,
    rohstoff_e4,
    mahllohn_e4: e.mahllohn_e4,
    nachprodukt_erloes_e4,
    wz_bereinigt_e4,
    ab_hof_e4,
  };
}

/* ── Fracht ─────────────────────────────────────────────────────────────── */

export interface FrachtEingabe {
  /** Einfache Entfernung. Die Formel rechnet Hin- und Rückfahrt. */
  entfernung_km: number;
  /** Tagessatz Personal, e4. */
  personal_tag_e4: number;
  /** Tagessatz Fahrzeug, e4. */
  lkw_tag_e4: number;
  /** Verbrauch je 100 km, e4 (280000 = 28,0 l). */
  verbrauch_e4: number;
  /** Dieselpreis je Liter, e4. */
  diesel_e4: number;
  /** Mautsatz je Kilometer, e4. */
  maut_e4_km: number;
  /** Tatsächliche Lademenge — bestimmt den Preis je Tonne. */
  lademenge_kg: number;
}

export interface FrachtErgebnis {
  strecke_km: number;
  /** Wiederholt die Eingabe: ohne sie ist „je Tonne" nicht lesbar. */
  lademenge_kg: number;
  personal_e4: number;
  lkw_e4: number;
  kraftstoff_e4: number;
  maut_e4: number;
  tour_e4: number;
  je_tonne_e4: number;
  /** Anteil Diesel an den Tourkosten in bp — für die Gleitklausel. */
  dieselanteil_bp: number;
}

export function berechneFracht(e: FrachtEingabe): FrachtErgebnis {
  if (!Number.isFinite(e.entfernung_km) || e.entfernung_km < 0)
    throw new Error("Ungültige Entfernung.");
  if (!Number.isInteger(e.lademenge_kg) || e.lademenge_kg <= 0)
    throw new Error("Lademenge fehlt.");

  const strecke_km = e.entfernung_km * 2;

  // Entfernung × 2 ÷ 100 × Verbrauch × Dieselpreis.
  // Zuerst multiplizieren, erst am Ende teilen — sonst summieren sich
  // Rundungsfehler über drei Schritte.
  const kraftstoff_e4 = roundDiv(strecke_km * e.verbrauch_e4 * e.diesel_e4, 100 * 10_000);
  const maut_e4 = roundDiv(strecke_km * e.maut_e4_km, 1);

  const tour_e4 = e.personal_tag_e4 + e.lkw_tag_e4 + kraftstoff_e4 + maut_e4;
  const je_tonne_e4 = roundDiv(tour_e4 * 1000, e.lademenge_kg);

  return {
    strecke_km,
    lademenge_kg: e.lademenge_kg,
    personal_e4: e.personal_tag_e4,
    lkw_e4: e.lkw_tag_e4,
    kraftstoff_e4,
    maut_e4,
    tour_e4,
    je_tonne_e4,
    dieselanteil_bp: tour_e4 ? roundDiv(kraftstoff_e4 * 10_000, tour_e4) : 0,
  };
}

/* ── Finanzierung ───────────────────────────────────────────────────────── */

/**
 * Kosten des Zahlungsziels. Kaufmännisch mit 360 Tagen gerechnet.
 */
export function berechneFinanzierung(
  basis_e4: number,
  zinssatz_bp: number,
  zahlungsziel_tage: number,
): number {
  if (zinssatz_bp <= 0 || zahlungsziel_tage <= 0) return 0;
  return roundDiv(basis_e4 * zinssatz_bp * zahlungsziel_tage, 10_000 * 360);
}

/* ── Gesamtkette ────────────────────────────────────────────────────────── */

export interface PreisEingabe extends MehlEingabe {
  fracht: FrachtEingabe | null;
  /** Bei Selbstabholung fällt keine Fracht an. */
  mit_fracht: boolean;
  zinssatz_bp: number;
  zahlungsziel_tage: number;
}

export interface PreisErgebnis {
  mehl: MehlErgebnis;
  fracht: FrachtErgebnis | null;
  fracht_je_t_e4: number;
  finanzierung_e4: number;
  /** Je Tonne Mehl, frei Haus bzw. ab Hof. */
  preis_je_t_e4: number;
  /** Dieselbe Zahl je 100 kg — die Einheit der übrigen Anwendung. */
  preis_je_100kg_e4: number;
}

export function berechnePreis(e: PreisEingabe): PreisErgebnis {
  const mehl = berechneMehlAbHof(e);

  const fracht = e.mit_fracht && e.fracht ? berechneFracht(e.fracht) : null;
  const fracht_je_t_e4 = fracht?.je_tonne_e4 ?? 0;

  const finanzierung_e4 = berechneFinanzierung(
    mehl.ab_hof_e4 + fracht_je_t_e4,
    e.zinssatz_bp,
    e.zahlungsziel_tage,
  );

  const preis_je_t_e4 = mehl.ab_hof_e4 + fracht_je_t_e4 + finanzierung_e4;

  return {
    mehl,
    fracht,
    fracht_je_t_e4,
    finanzierung_e4,
    preis_je_t_e4,
    // Die Anwendung rechnet in €/100 kg. Faktor 10, nicht 100.
    preis_je_100kg_e4: roundDiv(preis_je_t_e4, 10),
  };
}
